/**
 * Who is who among the enemy (10 §2): what each soldier wears, and which of them the mission is about.
 * `Enemy` itself never says "I am the general" - the sim names him by id, in `world.objective`
 * (`targetEnemyId` for a kill / loot mission, `officerId` for the man to be taken alive), and both ids are
 * -1 until the first ground tick, or until a dormant man's timed event puts him on the map. So a look is
 * a function of the man AND the moment: `Actors` asks every frame, and rebuilds the one soldier whose
 * `role` has changed.
 */

import type { Enemy, MissionDef, ObjectiveKind, World } from '../../sim/types';
import { type SoldierLook, targetLook } from '../characters/model';
import type { TimeOfDay } from '../palette';
import { captiveLook } from './Captives';

const SKINS = ['skinA', 'skinB', 'skinC'] as const;

/** What `Actors` needs to know about the mission, and nothing more: it never sees the `MissionDef`. */
export interface ActorCast {
  /** Decides what marks out the `targetEnemyId` man: the general's greatcoat, the courier's satchel. */
  objectiveKind: ObjectiveKind;
  /** What the capture target is once taken (`SpawnDef.captiveKind`): a scientist wears his lab coat from the start. */
  captureKind: 'officer' | 'scientist';
  /** How hard anything self-lit on a machine burns (the mini-bunker's sensor slot). */
  timeOfDay: TimeOfDay;
}

export function castOf(def: MissionDef): ActorCast {
  const capture = def.spawns.find((s) => s.captureTarget === true);
  return {
    objectiveKind: def.objective.kind,
    captureKind: capture?.captiveKind ?? 'officer',
    timeOfDay: def.timeOfDay,
  };
}

/** `plain`: one of the garrison. `target`: the man a kill / loot mission is about. `scientist`: to be taken alive. */
export type ActorRole = 'plain' | 'target' | 'scientist';

/** A soldier by his kind and his kit alone (02 §9.1); an unarmed man (`SpawnDef.unarmed`) carries no rifle. */
export function enemyLook(e: Enemy): SoldierLook {
  const skin = SKINS[e.id % SKINS.length] ?? 'skinA';
  const rifle = e.unarmed !== true;
  if (e.kind === 'officer') return { side: 'officer', skin, headgear: 'cap', pack: false, rifle };
  if (e.kind === 'elite') return { side: 'enemy', skin, headgear: 'beret', pack: false, rifle };
  if (e.kind === 'flamethrower') return { side: 'enemy', skin, headgear: 'helmet', pack: false, kit: 'flamethrower' };
  if (e.kind === 'rocketTrooper') return { side: 'enemy', skin, headgear: 'helmet', pack: false, kit: 'rocket' };
  return { side: 'enemy', skin, headgear: 'helmet', pack: false, rifle };
}

/** Which of the mission's people this man is, as far as the sim has said so yet. Without a cast: nobody special. */
export function roleOf(e: Enemy, world: World, cast: ActorCast | null): ActorRole {
  if (cast === null) return 'plain';
  const o = world.objective;
  if (e.id === o.officerId && cast.captureKind === 'scientist') return 'scientist';
  if (e.id === o.targetEnemyId && targetLook(enemyLook(e), cast.objectiveKind).accessory !== undefined) return 'target';
  return 'plain';
}

export function lookFor(e: Enemy, role: ActorRole, cast: ActorCast | null): SoldierLook {
  // The same white coat he will wear as a captive: he must be recognisable BEFORE he is taken.
  if (role === 'scientist') return captiveLook({ id: e.id, kind: 'scientist' });
  if (role === 'target' && cast !== null) return targetLook(enemyLook(e), cast.objectiveKind);
  return enemyLook(e);
}
